/**
 * A1 · RANGO DE FECHAS UNIFICADO. PURO.
 *
 * La auditoría A0 encontró el mismo concepto implementado de tres formas
 * distintas: Trabajos con tres variables sueltas (`fecha`, `desde`, `hasta`),
 * Despachos con un objeto, Clientes con un booleano `esteMes`. Mientras siguiera
 * así no había forma de tener un formato de URL único.
 *
 * LA DECISIÓN QUE IMPORTA: los tokens relativos se resuelven AL LEER, nunca al
 * guardar. Un enlace compartido o un filtro guardado como "esta semana" tiene
 * que seguir significando esta semana la semana que viene. Si guardáramos las
 * fechas ya calculadas, el enlace envejecería sin que nadie se diera cuenta —
 * y eso es justo lo que la Fase A2 (filtros guardados) va a necesitar.
 */

export type TokenRango = 'hoy' | 'manana' | 'hoy-manana' | 'semana' | 'mes' | 'todo';

export interface OpcionRango {
  token: TokenRango;
  etiqueta: string;
}

/**
 * Los tokens que cubren las tres formas viejas MAS el "manana" que Trabajos ya
 * tenia. Ese casi se pierde en la unificacion: en un negocio de instalaciones
 * "que hay manana" es de los filtros mas usados, y dejarlo caer habria sido una
 * regresion silenciosa que nadie reporta pero todos sufren.
 */
export const RANGOS: readonly OpcionRango[] = [
  { token: 'hoy', etiqueta: 'Hoy' },
  { token: 'manana', etiqueta: 'Mañana' },
  { token: 'hoy-manana', etiqueta: 'Hoy y mañana' },
  { token: 'semana', etiqueta: 'Esta semana' },
  { token: 'mes', etiqueta: 'Este mes' },
  { token: 'todo', etiqueta: 'Todo' },
] as const;

const TOKENS = new Set(RANGOS.map((r) => r.token));

/** ¿Es un token que conocemos? PURO. */
export function esRangoValido(raw: string | undefined | null): boolean {
  return typeof raw === 'string' && TOKENS.has(raw as TokenRango);
}

/** Lo que se guarda en la URL. */
export interface RangoEnUrl {
  rango?: TokenRango;
  desde?: string;
  hasta?: string;
}

/** Lo que la pantalla usa para consultar. `null` = sin acotar por ese lado. */
export interface RangoResuelto {
  desde: string | null;
  hasta: string | null;
}

/** "YYYY-MM-DD" válida, o null. PURO. */
function fechaValida(raw: string | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  // El formato puede ser correcto y la fecha no existir (2026-13-45).
  const d = new Date(`${t}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10) === t ? t : null;
}

/** Fecha local (zona del negocio) como "YYYY-MM-DD". */
const MS_DIA = 24 * 60 * 60 * 1000;

const TZ = 'America/Santo_Domingo';
function local(fecha: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(fecha);
}

/** Día de la semana local, 0 = domingo. */
function diaSemanaLocal(fecha: Date): number {
  const nombre = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short' }).format(fecha);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(nombre);
}

/**
 * Resuelve el rango a dos fechas concretas. PURO.
 *
 * El token relativo MANDA sobre las fechas explícitas: si llegan los dos, uno
 * tiene que ganar de forma predecible, y gana el que el usuario eligió del
 * selector.
 */
export function resolverRango(entrada: RangoEnUrl, ahora: Date = new Date()): RangoResuelto {
  const token = esRangoValido(entrada.rango) ? (entrada.rango as TokenRango) : null;

  if (token === 'todo') return { desde: null, hasta: null };

  if (token === 'hoy') {
    const h = local(ahora);
    return { desde: h, hasta: h };
  }

  if (token === 'manana') {
    const m = local(new Date(ahora.getTime() + MS_DIA));
    return { desde: m, hasta: m };
  }

  // La ventana con la que se planifica el despacho: lo de hoy que falta y lo de
  // manana que hay que preparar. Era el default de esa pantalla.
  if (token === 'hoy-manana') {
    return { desde: local(ahora), hasta: local(new Date(ahora.getTime() + MS_DIA)) };
  }

  if (token === 'semana') {
    // Arranca el LUNES, no "hace 7 días": es como la gente habla de "esta
    // semana", y hace que el número coincida con lo que espera ver.
    const dia = diaSemanaLocal(ahora);
    const alLunes = dia === 0 ? 6 : dia - 1;
    return { desde: local(new Date(ahora.getTime() - alLunes * MS_DIA)), hasta: local(ahora) };
  }

  if (token === 'mes') {
    return { desde: `${local(ahora).slice(0, 7)}-01`, hasta: local(ahora) };
  }

  // Sin token (o con uno inventado): se usan las fechas explícitas si sirven.
  let desde = fechaValida(entrada.desde);
  let hasta = fechaValida(entrada.hasta);

  // Un rango invertido no devuelve nada y la pantalla dice "sin datos", que es
  // peor que corregirlo: el dueño se queda creyendo que no vendió nada.
  if (desde && hasta && desde > hasta) [desde, hasta] = [hasta, desde];

  return { desde, hasta };
}

/**
 * `RangoResuelto` → lo que espera la API (`{ from, to }` en ISO absoluto). PURO.
 *
 * Las pantallas razonan en días locales ("el 11 de septiembre") y el backend en
 * instantes. Esta es la única frontera donde se traduce, así que el resto del
 * código no tiene que pensar en husos horarios.
 *
 * `to` es el último milisegundo del día, no las 00:00 del siguiente: así el
 * filtro incluye todo el día final, que es lo que el usuario espera al elegir
 * "del 1 al 11".
 */
export function aRangoConsulta(r: RangoResuelto): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (r.desde) out.from = new Date(`${r.desde}T00:00:00${OFFSET}`).toISOString();
  if (r.hasta) {
    const fin = new Date(`${r.hasta}T00:00:00${OFFSET}`);
    fin.setDate(fin.getDate() + 1);
    fin.setMilliseconds(-1);
    out.to = fin.toISOString();
  }
  return out;
}

/**
 * Desfase fijo de República Dominicana. No tiene horario de verano, así que un
 * offset constante es correcto todo el año y evita depender de la zona del
 * navegador —que en un técnico viajando podría no ser la del negocio.
 */
const OFFSET = '-04:00';
